import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { PlayCircle, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { VideosGridSkeleton } from "@/components/Skeletons";

const API_URL = window.location.origin;

// Helper function to decode HTML entities
const decodeHtmlEntities = (text) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

export default function VideosPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/youtube/videos`);
      const data = await response.json();
      setVideos(data.videos || []);
      setIsLive(data.isLive || false);
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get the live video if streaming
  const liveVideo = videos.find(v => v.isLive);

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-bone to-white">
        {/* Hero Section */}
        <section className="bg-navy text-white py-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
          </div>
          <div className="container-page relative z-10">
            <div className="text-center">
              {isLive && (
                <div className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full mb-4 animate-pulse">
                  <Radio className="h-4 w-4" />
                  <span className="font-semibold">STREAMING LIVE NOW</span>
                </div>
              )}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-4">
                Videos & Live Streams
              </h1>
              <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto mb-6">
                "For the word of God is alive and active. Sharper than any double-edged sword" 
                <span className="block mt-2 text-gold font-semibold">- Hebrews 4:12</span>
              </p>
              <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
                Experience Spirit-filled worship, powerful teachings, and prophetic declarations from Word Alive Global Center
              </p>
              {!isLive && (
                <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={() => window.open('https://www.youtube.com/@WordAliveGlobalCenter', '_blank')}
                    className="bg-gold text-navy hover:bg-gold/90 px-8 py-6 text-base uppercase tracking-wider"
                  >
                    <PlayCircle className="mr-2 h-5 w-5" />
                    Subscribe on YouTube
                  </Button>
                  <Button 
                    onClick={() => window.open('https://www.facebook.com/WordAliveGlobalCenter/', '_blank')}
                    variant="outline"
                    className="border-white text-white hover:bg-white hover:text-navy px-8 py-6 text-base uppercase tracking-wider"
                  >
                    Watch Live on Facebook
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Featured Live Stream Player */}
        {isLive && liveVideo && (
          <section className="py-12 bg-gradient-to-b from-red-50 to-white">
            <div className="container-page">
              <div className="mb-8 text-center">
                <div className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-full mb-4 animate-pulse">
                  <Radio className="h-5 w-5" />
                  <span className="font-bold text-lg">LIVE NOW</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy mb-2">
                  {decodeHtmlEntities(liveVideo.title)}
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  {decodeHtmlEntities(liveVideo.description).substring(0, 150)}...
                </p>
              </div>

              {/* Large Live Player */}
              <div className="bg-black rounded-xl overflow-hidden shadow-2xl mb-6">
                <div className="relative pb-[56.25%]">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${liveVideo.id}?autoplay=1&mute=0`}
                    title={liveVideo.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>

              {/* Streaming Options */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></span>
                  <span className="font-medium">Streaming on YouTube</span>
                </div>
                <Button 
                  onClick={() => window.open('https://www.wordaliveglobal.org/live', '_blank')}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                >
                  <Radio className="mr-2 h-4 w-4" />
                  Watch on Owncast (Backup)
                </Button>
                <Button 
                  onClick={() => window.open('https://www.facebook.com/WordAliveGlobalCenter/', '_blank')}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                >
                  Watch on Facebook
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* Videos Grid - Hidden when live */}
        {!isLive && (
          <section className="py-16">
          <div className="container-page">
            {loading ? (
              <div className="space-y-12">
                <div className="text-center mb-12">
                  <Skeleton className="h-10 w-96 mx-auto bg-navy/8 mb-4" />
                  <Skeleton className="h-5 w-64 mx-auto bg-navy/5" />
                </div>
                <VideosGridSkeleton count={6} />
              </div>
            ) : videos.length === 0 ? (
              <div className="text-center py-16">
                <PlayCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No videos available</h3>
                <p className="text-gray-600">Check back soon for Spirit-filled content!</p>
              </div>
            ) : (
              <>
                <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy mb-4">
                    Latest Messages, Worship & Live Streams
                  </h2>
                  <p className="text-lg text-gray-600">
                    "Come and behold the beauty of the Lord" - Psalm 27:4
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {videos.map((video) => (
                    <div 
                      key={video.id} 
                      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300"
                    >
                      <div className="relative pb-[56.25%]">
                        <iframe
                          className="absolute top-0 left-0 w-full h-full"
                          src={`https://www.youtube.com/embed/${video.id}`}
                          title={video.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          {video.isLive && (
                            <span className="bg-red-600 text-white px-3 py-1 rounded text-xs font-bold animate-pulse">
                              🔴 LIVE NOW
                            </span>
                          )}
                          {video.type === "SHORT" && !video.isLive && (
                            <span className="bg-purple-600 text-white px-3 py-1 rounded text-xs font-bold">
                              ⚡ SHORT
                            </span>
                          )}
                          {video.type === "VIDEO" && !video.isLive && (
                            <span className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-bold">
                              🎥 VIDEO
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-navy mb-2 line-clamp-2">
                          {decodeHtmlEntities(video.title)}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                          {decodeHtmlEntities(video.description)}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{new Date(video.publishedAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="bg-navy text-white py-16">
          <div className="container-narrow text-center">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-6">
              Encounter Christ Today
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Join us for live worship every Sunday at 10:30 AM CST or visit our YouTube channel for archived teachings, prophetic words, and more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/events'}
                className="bg-gold text-navy hover:bg-gold/90 px-8 py-6 text-base uppercase tracking-wider"
              >
                View Events Calendar
              </Button>
              <Button 
                onClick={() => window.location.href = '/about'}
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-navy px-8 py-6 text-base uppercase tracking-wider"
              >
                Learn About WAGC
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
